import classNames from 'classnames';
import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

// components
import CartItemMembership from './CartItemMembership';
import CartItemEvent from './CartItemEvent';
import MemberInitialsWithCircle from '@atoms/MemberInitialsWithCircle';
import EditIcon from '@icons/EditIcon';
import TrashIcon from '@icons/TrashIcon';
import CaretDownIcon from '@icons/CaretDownIcon';
import CaretUpIcon from '@icons/CaretUpIcon';
import { BodyMedium, ButtonForIcon, DivWithShadowAlternate } from '@styled';

// functionality
import { useDeleteCartItemModal } from '../../../modals/DeleteCartItemModal';
import { LoadingContext } from '@context/LoadingProvider';
import productTypes from '@general/productTypes';
import { updateCartEventCredit, deleteCartItem } from '@general/api/cartAPI';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventToRegisterSessionsFor from '@hooks/useEventToRegisterSessionsFor';
import useMemberFromAPI from '@hooks/apiLegacy/useMemberFromAPI';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';

const ComponentContainerStyled = styled(DivWithShadowAlternate)`
    padding: 24px;
`;

const CartItemGroupContainerStyled = styled.div`
    &:not(:last-child) {
        margin-bottom: 32px;
    }
`;

const CartItemContainerStyled = styled.div`
    &:not(:last-child) {
        margin-bottom: 32px;
    }
`;

const CartMemberPanel = ({
    memberId,
    memberName,
    isOpen = false,
    itemsGroupedByType,
    onComponentLoaded = () => {},
    setOptionalFullProgramEvents,
}) => {
    // -----------------------------
    // State
    // -----------------------------

    const [itemNames, setItemNames] = useState();
    const [isPanelOpen, setIsPanelOpen] = useState(isOpen);
    const [cardsLoaded, setCardsLoaded] = useState([]);

    // -----------------------------
    // Hooks
    // -----------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const history = useHistory();
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { member } = useMemberFromAPI({ id: memberId });
    const { eventToRegisterSessionsFor } = useEventToRegisterSessionsFor();
    const { openModal: openDeleteConfirmationModal } = useDeleteCartItemModal();
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!itemsGroupedByType) {
            return null;
        }

        const itemNameArray = itemsGroupedByType.reduce((itemNamesWorking, itemType) => {
            if (itemType.type === productTypes.MEMBERSHIP) {
                return [...itemNamesWorking, itemType.items[0].name];
            }

            if (itemType.type === productTypes.EVENT) {
                return [...itemNamesWorking, ...itemType.items.map(item => item.name)];
            }

            return itemNamesWorking;
        }, []);

        setItemNames(itemNameArray);
    }, [itemsGroupedByType]);

    useEffect(() => {
        setIsPanelOpen(isOpen);
    }, [isOpen]);

    // -----------------------------
    // Functions
    // -----------------------------
    const removeEventCredit = async () => {
        try {
            await updateCartEventCredit({
                cartId: cart.id,
                viewerId: viewer.recordId,
                shouldApplyCredit: false,
            });
        } catch (error) {
            alert('Error removing event credit.');
        }
    };

    const removeAllItemsForMember = () => {
        openDeleteConfirmationModal(`all cart items for ${memberName}`, async () => {
            setIsLoading(true);

            const itemsToDelete = cart.items.filter(item => item.member_id === memberId);

            for (const item of itemsToDelete) {
                await deleteCartItem({
                    cartId: cart.id,
                    cartItemId: item.id,
                    viewerId: viewer.recordId,
                });
            }

            setOptionalFullProgramEvents(null);
            await removeEventCredit();

            setIsLoading(false);
        });
    };

    const trackLoadedCards = ({ productType, productId }) => {
        const alreadyLoaded = !!cardsLoaded.find(
            card => card.productType === productType && card.productId === productId
        );

        if (alreadyLoaded) {
            return;
        }

        const newArray = [
            ...cardsLoaded,
            {
                productType,
                productId,
            },
        ];

        const cardCount = itemsGroupedByType.reduce((tally, itemType) => {
            return tally + itemType.items.length;
        }, 0);

        if (newArray.length === cardCount) {
            onComponentLoaded();
        }

        setCardsLoaded(newArray);
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (!viewer || !itemNames || isUndefined(eventToRegisterSessionsFor)) {
        return null;
    }

    const memberNameSplit = memberName.split(' ');

    return (
        <ComponentContainerStyled>
            <div className="flex items-center">
                <MemberInitialsWithCircle
                    firstName={memberNameSplit.length > 0 ? memberNameSplit[0] : ''}
                    lastName={memberNameSplit.length > 1 ? memberNameSplit[memberNameSplit.length - 1] : ''}
                />

                <div className="flex-1 ml-0.75rem mt-0.325rem">
                    <BodyMedium className="text-admiral">{memberName}</BodyMedium>

                    {isScreenMinMd &&
                        itemNames.map((itemName, index) => <BodyMedium key={index}>{itemName}</BodyMedium>)}
                </div>

                <div
                    className="flex"
                    style={{
                        marginRight: isScreenSmDown ? '-10px' : '0',
                    }}
                >
                    <ButtonForIcon type="button" onClick={() => history.push(`/cart/account-info/${memberId}`)}>
                        <EditIcon />
                    </ButtonForIcon>

                    <ButtonForIcon type="button" onClick={() => removeAllItemsForMember()}>
                        <TrashIcon />
                    </ButtonForIcon>

                    <ButtonForIcon type="button" onClick={() => setIsPanelOpen(!isPanelOpen)}>
                        {!isPanelOpen && <CaretDownIcon />}
                        {isPanelOpen && <CaretUpIcon />}
                    </ButtonForIcon>
                </div>
            </div>

            <div
                className={classNames('pt-1rem', {
                    hidden: !isPanelOpen,
                })}
            >
                {itemsGroupedByType.map(itemTypeGroup => (
                    <CartItemGroupContainerStyled key={itemTypeGroup.type}>
                        <BodyMedium className="mb-1rem">
                            {itemTypeGroup.type === productTypes.MEMBERSHIP && <>Membership</>}
                            {itemTypeGroup.type === productTypes.EVENT && <>Events</>}
                        </BodyMedium>

                        {itemTypeGroup.items.map(item => (
                            <CartItemContainerStyled key={item.product_id}>
                                {itemTypeGroup.type === productTypes.MEMBERSHIP && (
                                    <CartItemMembership
                                        membershipDataForMember={item}
                                        onComponentLoaded={() => {
                                            trackLoadedCards({
                                                productType: itemTypeGroup.type,
                                                productId: item.product_id,
                                            });
                                        }}
                                    />
                                )}

                                {itemTypeGroup.type === productTypes.EVENT && (
                                    <CartItemEvent
                                        eventDataForMember={item}
                                        onComponentLoaded={() => {
                                            trackLoadedCards({
                                                productType: itemTypeGroup.type,
                                                productId: item.product_id,
                                            });
                                        }}
                                        setOptionalFullProgramEvents={setOptionalFullProgramEvents}
                                    />
                                )}
                            </CartItemContainerStyled>
                        ))}
                    </CartItemGroupContainerStyled>
                ))}
            </div>
        </ComponentContainerStyled>
    );
};

CartMemberPanel.propTypes = {
    memberId: PropTypes.number.isRequired,
    memberName: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    itemsGroupedByType: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.oneOf([productTypes.MEMBERSHIP, productTypes.EVENT]).isRequired,
            items: PropTypes.arrayOf(PropTypes.object).isRequired,
        })
    ),
    onComponentLoaded: PropTypes.func,
    setOptionalFullProgramEvents: PropTypes.func,
};

export default CartMemberPanel;
